/* Shared shapes. No DOM, no Lit — this layer is what would survive a future
/* Shared shapes. No DOM, no Lit — this layer is what would survive a future
   React Native rewrite, so keep it framework-free.

   Two halves, and the split is the point:

   - **The API contract is not written here any more.** It is generated from
     app/schemas.py into packages/api-types and re-exported below, so the server
     and every client read one definition. This file used to hand-declare those
     interfaces, and they had drifted: `Item` was missing `cities` and `mentions`
     (always present on a search result), and `Me.display_name`/`photo_url` were
     typed nullable when `_me_payload` coalesces both to "". Regenerate with
     `py scripts/gen_api_types.py`; tests/test_schemas.py fails if a model stops
     matching the real response.
   - **Everything below the re-exports is frontend-only** — intents, workspace
     definitions, tab items, theme preference. None of it exists on the server
     and none of it belongs in a generated file.

   Imports elsewhere are unchanged: this is still the one module the app imports
   its shapes from. */

// ── the API contract, generated ──────────────────────────────
// Named exactly as the app already used them, so nothing above this line moved.
export type {
  AuthConfig, Category, DetailMention, DetailPlace, Facets, Item, ItemDetail,
  Me, Meta, ProposedCategory, SearchItem, Venue, VenueReel,
} from '../../../packages/api-types/index.js';

/**
 * One aggregated Index entry (GET /api/mentions) — a named thing the caller's
 * items recommend, grouped by `(name, kind)` across their whole library.
 *
 * Called `MentionRow` server-side to keep it distinct from the per-item
 * `DetailMention`; kept as `Mention` here because that is what Index calls it.
 *
 * `n` is 1 for ~91% of rows, which is why the UI only shows the count when it is
 * >= 2 (constitution §23 — surface only actionable information). That reasoning
 * is a UI decision, so it lives here rather than in the schema.
 */
export type { MentionRow as Mention } from '../../../packages/api-types/index.js';

// Available and not yet used by the web client: VideoResolved, TokenSession,
// Health, Ok, Accepted, ReelDeleted, CategoryDeleted, OnboardingSuggestions,
// ProposedTaxonomy, OnboardingCompleted, AskAnswer, AskCitation. Import them
// from packages/api-types when a surface needs them.

// ── frontend-only, from here down ────────────────────────────

/**
 * A message from one part of the app to another that is not navigation alone.
 *
 * Workspaces cannot see each other — they are siblings in the shell's outlet,
 * each in its own shadow root — and Detail sits above all of them. Two things
 * genuinely have to cross that gap, so they cross through one typed channel
 * that the shell delivers, rather than a global event bus:
 *
 * - `show-place`: Detail's place chip means "take me to this pin", which is a
 *   workspace switch *plus* a selection the target has to honour.
 * - `item-deleted`: removing an item from Detail invalidates cached data in
 *   every workspace that is already mounted. v1 did the same thing by nulling
 *   `state.mentions` / `state.places`; this makes it explicit and typed.
 * - `open-search`: the tab bar's search entry (ADR 0019). The band that owns the
 *   field lives inside a workspace, and only the workspace knows what has to
 *   happen first — Map swaps to its list view and closes a venue sheet. Unlike
 *   the other two this one is ADDRESSED to the active workspace, not broadcast;
 *   see the shell's `send`.
 */
export type Intent =
  | { kind: 'show-place'; mapPlaceId: number }
  | { kind: 'open-search' }
  | { kind: 'item-deleted'; id: number };

/**
 * What an open playlist has in common — the reason these items are together.
 *
 * Every call site knows this and used to discard it: Index holds the entry, Map
 * holds the venue, Library holds the category chip or the query. Detail was told
 * only which workspace opened it, so the top bar said a bare "Index".
 *
 * This is also the more useful half of the signal the "3 of 12" counter carried
 * before it was removed (decision 3). Where you are in a list matters less than
 * what the list *is*, which is the thing you forget three items in.
 *
 * Optional throughout: an unfiltered Library, and a cold deep link with no
 * origin at all, genuinely have no thread and fall back to the workspace name.
 */
export interface PlaylistSource {
  /** Workspace id, which the shell resolves to an icon. */
  workspace: string;
  /** The thread itself: a venue, an Index entry, a category, a query. */
  label: string;
}

/** A workspace that accepts `Intent`s. Implementing it is entirely optional. */
export interface IntentTarget {
  applyIntent(intent: Intent): void;
}

/**
 * How a workspace describes itself to the shell.
 *
 * This is the seam that keeps `g-header` ignorant of which workspaces exist: a
 * workspace declares whether it accepts a search query, and the shell enables
 * or disables the header field accordingly. Adding a workspace never edits the
 * header. Dependencies point layout → workspace, never the reverse.
 */
export interface WorkspaceDef {
  /** URL segment and the element's identity, e.g. "library". */
  id: string;
  /** Rail label. */
  label: string;
  /** Icon name registered in g-icon. */
  icon: string;
  /** Custom element tag to mount. */
  tag: string;
  /** Rail zone: primary workspaces on top, utility at the bottom. */
  zone: 'top' | 'bottom';
  /** Constitution §5 — search only exists where it's useful. */
  searchable: boolean;
  /** Placeholder when searchable; the field is dimmed and inert when not. */
  searchPlaceholder?: string;
  /**
   * Fill the outlet instead of scrolling inside it.
   *
   * Every workspace so far is a document: it grows as tall as its content and
   * the shell's `#outlet` scrolls it. Map is the first that must be exactly as
   * tall as the viewport and scroll only its own panel — a map that scrolls off
   * the top is not a map.
   *
   * Declared here rather than detected in the shell for the same reason
   * `searchable` is: adding a workspace must never edit layout code.
   */
  fill?: boolean;
  /**
   * Render this workspace's tab as the signed-in user's avatar instead of its
   * icon (ADR 0009 decision 2).
   *
   * Declared here for the same reason as `fill` and `searchable`: g-tabbar
   * renders the defs it is given and must never learn that a workspace called
   * "settings" exists. The rail ignores it — desktop still has the header's
   * avatar, so identity is already on screen there.
   */
  identity?: boolean;
  /**
   * This workspace can reload its own data, so the shell offers pull-to-refresh
   * over it (ADR 0010).
   *
   * Declared rather than assumed for the same reason as `searchable`: the shell
   * owns the gesture and the indicator, the workspace owns what "refresh" means.
   * A workspace that opts in MUST expose `refresh(): Promise<void>`; there is no
   * way to say that in a type the shell can hold, since it holds elements by
   * tag name, so `Refreshable` below is the shape it duck-types against.
   */
  refreshable?: boolean;
}

/** What `WorkspaceDef.refreshable` promises the shell it can call. */
export interface Refreshable {
  refresh(): Promise<void>;
}

/**
 * A tab that does something instead of going somewhere (ADR 0019).
 *
 * The tab bar's one non-destination entry. It is NOT a workspace: no route, no
 * rail slot, and never a selected state, because there is nowhere to be selected
 * at. Search exists here because the band's own magnifier sits at the top of the
 * screen, which is the furthest point from a thumb — this is a second way to
 * reach the same field, not a replacement for it.
 *
 * `kind` is the discriminant against WorkspaceDef, which has none.
 */
export interface TabAction {
  kind: 'action';
  /** Identifies the action in the event the bar emits. */
  id: string;
  label: string;
  icon: string;
}

/** What the tab bar renders: destinations, plus the odd action among them. */
export type TabItem = WorkspaceDef | TabAction;

/** Narrowing helper, so callers don't restate the discriminant. */
export const isTabAction = (item: TabItem): item is TabAction =>
  'kind' in item && item.kind === 'action';

export type ThemePref = 'auto' | 'light' | 'dark';
